/// <reference types="vite/client" />
import {
  HeadContent,
  Link,
  Scripts,
  createRootRouteWithContext,
  Outlet,
  RouterProvider,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as React from "react";
import { DefaultCatchBoundary } from "~/components/DefaultCatchBoundary";
import { NotFound } from "~/components/NotFound";
import parcelUiCss from "@abodeminehq/parcel-ui/parcel-ui.css?url";
import appCss from "~/styles/app.css?url";

import { seo } from "~/utils/seo";
import { createLogtoClient } from "~/lib/logto/logto";
// import { LogtoProvider } from '@logto/react'
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PostHogProvider } from "posthog-js/react";
import {
  AuthProvider,
  useAuthContext,
  type AuthContextType,
} from "~/contexts/auth-context";
import { runtimeConfig } from "~/lib/runtime-config";
import { ANALYTICS_SOURCE } from "~/lib/analytics";
import { queryClient } from "~/lib/queries/query";
import { Toaster } from "sonner";
import { AbodemineFullScreenLoader } from "~/components/brand/abodemine-loader";
import { useEffect, useMemo } from "react";
import { OrgProvider } from "~/contexts/org-context";
import { useDynamicConfig } from "~/lib/queries/meta";
import {ThemeProvider, setLogtoAccessTokenProviderForParcelUI, useTheme} from "@abodeminehq/parcel-ui"
// Define the router context type - we'll use this for future context needs
interface RouterContext {
  auth: AuthContextType | null;
  // Add any router-level context here in the future
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1, maximum-scale=1,",
      },
      ...seo({
        title: "AbodeMine API Dashboard",
        description: `Manage your AbodeMine API keys and usage.`,
      }),
    ],
    scripts: [
      // Load runtime configuration first
      {
        src: `/api/runtime-config`,
      },
      // Theme setup script
      {
        type: "module",
        children: /* js */ `
            let theme = localStorage.getItem('theme')
            if (!theme) {
              theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
            }

            if (theme === 'dark') {
              document.documentElement.classList.add('dark')
              document.documentElement.style.colorScheme = 'dark'
            } else {
              document.documentElement.classList.remove('dark')
              document.documentElement.style.colorScheme = 'light'
            }
          `,
      },
    ],
    links: [
      { rel: "stylesheet", href: parcelUiCss },
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
      { rel: "icon", href: "/favicon.ico" },
    ],
  }),
  errorComponent: DefaultCatchBoundary,
  notFoundComponent: () => <NotFound />,
  component: RootComponent,
  // scrollRestoration: false,
  // defaultHashScrollIntoView: false,
  context: () => {
    return {
      auth: null,
    };
  },
});

function RootComponent() {
  const logtoClient = useMemo(() => {
    const client = createLogtoClient();
    // parcel-ui's My Account settings call Logto's Account API directly and take
    // their token from this module-level provider, not from a React context —
    // without it every panel fails with "No access token provider set". Set
    // during render rather than in an effect so the settings route's first fetch
    // already has it, and browser-only: on the server this module scope is
    // shared across requests, so one visitor's client would answer for another.
    if (typeof window !== "undefined") {
      setLogtoAccessTokenProviderForParcelUI(client);
    }
    return client;
  }, []);
 
  return (
    <html>
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <AuthProvider logtoClient={logtoClient}>
            <OrgProvider>
              <ThemeProvider defaultTheme={'auto'}>
                <PostHogProvider
                  apiKey={runtimeConfig.VITE_PUBLIC_POSTHOG_KEY}
                  options={{
                    api_host: runtimeConfig.VITE_PUBLIC_POSTHOG_HOST,
                    defaults: "2025-05-24",
                    capture_exceptions: true,
                    debug: import.meta.env.MODE === "development",
                    // Matches the server client in `~/lib/posthog`. Done here
                    // instead of via `register()` super properties because
                    // `posthog.reset()` on signout would wipe those.
                    before_send: (event) => {
                      if (event) {
                        event.properties = {
                          ...event.properties,
                          source: ANALYTICS_SOURCE,
                        };
                      }
                      return event;
                    },
                  }}
                >
                  <Outlet />
                  
                  <Toaster />
                  <TanStackRouterDevtools position="bottom-right" />
                  <ReactQueryDevtools initialIsOpen={false} />
                </PostHogProvider>
              </ThemeProvider>
            </OrgProvider>
          </AuthProvider>
        </QueryClientProvider>
        <Scripts />
      </body>
    </html>
  );
}

//TEMP forse theme
function ForseTheme() {
  const { theme, setTheme } = useTheme()
  useEffect(() => {
    if(theme !== 'light') {
      setTheme('light')
    }
  }, [theme])
  return null
}
